import { useEffect, useState } from "react";
import type { DocumentSummary } from "./api";
import { mb, offlineDocs, planOfflineSize, removeOffline, saveForOffline, savedSet, storageInfo, useOfflineStatus, type SavedSet } from "./offline";

/** Per-project "Save for offline": working-set plans, thumbnails, sheet list, SOV and phase codes. */
export function OfflineCard({ projectId, documents }: { projectId: string; documents: DocumentSummary[] }) {
  const { online } = useOfflineStatus();
  const [saved, setSaved] = useState<SavedSet | null>(() => savedSet(projectId));
  const [prog, setProg] = useState<{ done: number; total: number; label: string } | null>(null);
  const [note, setNote] = useState<{ kind: "ok" | "error" | "warn"; text: string } | null>(null);
  const [space, setSpace] = useState<string>("");
  const docs = offlineDocs(documents);
  const stale = !!saved && (docs.some((d) => !saved.docs.find((s) => s.id === d.id && s.version === d.version)) || saved.docs.length !== docs.length);
  useEffect(() => { storageInfo().then((s) => setSpace(s.quota ? `${mb(s.usage)} used of ~${mb(s.quota)} allowed${s.persisted ? " · kept by the device" : ""}` : "")).catch(() => {}); }, [saved]);

  const run = async (force = false) => {
    setNote(null);
    if (!docs.length) return setNote({ kind: "error", text: "No plan sets to save." });
    const { bytes, warning } = await planOfflineSize(docs);
    if (warning && !force) return setNote({ kind: "warn", text: warning });
    setProg({ done: 0, total: 1, label: `Preparing ${mb(bytes)}…` });
    try {
      const s = await saveForOffline(projectId, docs, (done, total, label) => setProg({ done, total, label }));
      setSaved(s); setNote({ kind: "ok", text: `Saved ${s.docs.length} plan set${s.docs.length === 1 ? "" : "s"} (${mb(s.bytes)}). They open with no signal.` });
    } catch (e) { setNote({ kind: "error", text: (e as Error).message }); }
    finally { setProg(null); }
  };

  return (
    <div className="card offline-card" data-testid="offline-card">
      <div className="row wrap">
        <strong>Offline</strong>
        <span className="muted small">{saved ? `Saved ${new Date(saved.savedAt).toLocaleString()} · ${saved.docs.map((d) => d.name).join(", ")} · ${mb(saved.bytes)}` : `Save ${docs.length === documents.filter((d) => !d.archivedAt).length ? "this job's plan sets" : "the working set"} to use on site with no signal.`}</span>
        {online && !prog && <button type="button" className={`btn small ${!saved || stale ? "primary" : ""}`} onClick={() => void run()}>{saved ? (stale ? "Update offline copy" : "Refresh offline copy") : "Save for offline"}</button>}
        {saved && !prog && <button type="button" className="btn small" onClick={async () => { await removeOffline(projectId); setSaved(null); setNote(null); }}>Remove</button>}
      </div>
      {prog && <div className="progress"><div style={{ width: `${Math.round((prog.done / prog.total) * 100)}%` }} /><span>{prog.label} · {Math.round((prog.done / prog.total) * 100)}%</span></div>}
      {note && <div className={note.kind === "error" ? "error" : note.kind === "warn" ? "notice" : "ok-msg"}>{note.text}{note.kind === "warn" && <> <button type="button" className="btn small" onClick={() => void run(true)}>Save anyway</button></>}</div>}
      {stale && !prog && <div className="muted small">The working set changed since it was saved.</div>}
      {space && <div className="muted small">{space}</div>}
    </div>
  );
}

/** Header pill: Offline / N waiting to sync / Syncing. */
export function OfflinePill() {
  const s = useOfflineStatus();
  if (s.online && !s.waiting) return null;
  const text = !s.online ? (s.waiting ? `Offline · ${s.waiting} waiting to sync` : "Offline") : s.syncing ? `Syncing ${s.waiting}…` : s.error ? `${s.waiting} not synced: ${s.error}` : `${s.waiting} waiting to sync`;
  return <span className={`offline-pill${s.online ? "" : " off"}${s.error ? " err" : ""}`} role="status" data-testid="offline-pill">{text}</span>;
}
